import React, { useEffect, useState, useContext } from "react";
import ReactDomServer from "react-dom/server";
import L from "leaflet";

import IguMapPopup from "./_IguMapPopup";
import { iconBuilding } from "./_IguMapPopupIcon";
import IguToolbar from "../../mainMapToolbar/components/_IguToolbar";
import { createPointLayer } from "../utils/createPointLayer";
import { createGEOJsonLayer } from "../utils/createGJsonLayer";
import { CityContext } from "../../common/contexts/city-context";
import LinhasBusLegend from "./_IguMapLinhasBusLegend";
import SearchModal from "./_SearchModal";
import ODControls from "./_IguODControls";
import enableSmoothScrolling from "../utils/enableSmoothWheelZoom";
import { useTranslation } from "react-i18next";

const IguMap = (props) => {
    const cityCtx = useContext(CityContext);
    const cityID = cityCtx.cityData._id;
    const userCity = props.cidade;
    const { t } = useTranslation("iguMap");
    const {
        intSemData,
        intNoSemData,
        intMapData,
        viabilidadeSemData,
        contagemVeicData,
        contagemODData,
        contODData,
        projInterligacaoData,
        projSubtrechoData,
        areasSimulacaoData,
        rotasData,
        coordData,
        redeSemData,
        subRedeSemData,
        limiteMunicipioData,
        rotasDesempenhoData,
        linhasOnibusData,
        zoneamentoData,
        matrizODData,
        pontosOnibusData,
        topografiaData,
        imp1Data,
        imp2Data,
        imp3Data,
        imp4Data,
        imp5Data,
        imp6Data,
        imp7Data,
        imp8Data,
        imp9Data,
        semConjugadosData,
    } = props.layerData;

    const layerVisibility = {
        layerIntSemVisibility: true,
        layerIntNoSemVisibility: true,
        layerIntMapVisibility: true,
        layerIntAvalVisibility: true,
        layerContVisibility: true,
        layerContODVisibility: true,
        layerODVisibility: true,
        layerProjIntVisibility: true,
        layerProjSubVisibility: true,
        layerAreasSimVisibility: false,
        layerRotasVisibility: true,
        layerCoordVisibility: true,
        layerRedeSemVisibility: true,
        layerSubRedeSemVisibility: true,
        layerRotasDesempenhoVisibility: false,
        layerLinhasOnibusVisibility: false,
        layerZoneamentoVisibility: false,
        layerPontosOnibusVisibility: false,
        layerTopografiaVisibility: false,
        layerImp1Visibility: true,
        layerImp2Visibility: false,
        layerImp3Visibility: false,
        layerImp4Visibility: false,
        layerImp5Visibility: false,
        layerImp6Visibility: false,
        layerImp7Visibility: false,
        layerImp8Visibility: false,
        layerImp9Visibility: false,
        layerSemConjVisibility: true,
    };

    const iguLatitude = +userCity?.coord[0] ?? 0;
    const iguLongitude = +userCity?.coord[1] ?? 0;
    const iguZoom = 16;
    const iguAtribuicao =
        ' &copy <a target="OpenStreetMap contributors" href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>';
    const tileUrl1 = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
    const tileUrl2 = "http://a.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png";
    // const tileUrl2 = "https://tiles.wmflabs.org/bw-mapnik/{z}/{x}/{y}.png";

    const [panes, setPanes] = useState({});
    const [b1GroupTools, setB1GroupTools] = useState({});
    const [b2GroupTools, setB2GroupTools] = useState({});

    const legendItems = linhasOnibusData
        .map((feature) => ({
            linha: feature.properties.IGUNOME,
            cor: feature.properties.colorIndex,
            sentido: feature.properties.sentido,
        }))
        .sort((a, b) => (a.linha > b.linha ? 1 : -1));
    const [busLinhasLegendVisible, setBusLinhasLegendVisible] = useState(
        layerVisibility.layerLinhasOnibusVisibility
    );
    const [searchModalVisible, setSearchModalVisible] = useState(false);
    const [iguMap, setIguMap] = useState({});
    const [ODControlsVisible, setODControlsVisible] = useState(false);
    const [zoneamentoLayer, setZoneamentoLayer] = useState([]);

    useEffect(() => {
        // create map
        const tilelayer1 = L.tileLayer(tileUrl1, {
            attribution: iguAtribuicao,
        });
        const tilelayer2 = L.tileLayer(tileUrl2, {
            attribution: iguAtribuicao,
        });
        enableSmoothScrolling(L);
        const myMap = L.map("map", {
            center: [iguLatitude, iguLongitude],
            zoom: iguZoom,
            layers: tilelayer1,
            zoomControl: false,
            scrollWheelZoom: false,
            smoothWheelZoom: true, // enable smooth zoom
            smoothSensitivity: 2, // zoom speed. default is 1
        });
        setIguMap(myMap);

        // barra de escala
        const scale = L.control.scale({ imperial: false });
        scale.addTo(myMap);

        // intLegend
        const intLegend = L.control({ position: "bottomright" });
        intLegend.onAdd = () => {
            var div = L.DomUtil.create("div", "myMap-intLegend"),
                legendItems = [
                    t("intSem"),
                    t("intMap"),
                    t("contVeic"),
                    t("contOD"),
                ];
            div.innerHTML = '<div class="myMap-intLegend-title">Legenda</div>';
            const legendIcons = [
                "/img/interseccao-icon.svg",
                "/img/stoplights-fill.svg",
                "/img/camera-video-fill.svg",
                "/img/arrow-repeat.svg",
            ];
            for (var i = 0; i < legendItems.length; i++) {
                div.innerHTML +=
                    `<img src="${legendIcons[i]}" alt="bootstrap" width="18" height="18"/> ` +
                    legendItems[i] +
                    (legendItems[i + 1] ? "<br>" : "");
            }
            return div;
        };
        // initial popup
        const myMarker = L.marker([iguLatitude, iguLongitude], {
            icon: iconBuilding,
        }).addTo(myMap);
        const popup = ReactDomServer.renderToString(
            <IguMapPopup city={userCity} brasaoUrl={props.brasaoUrl} />
        );
        myMarker.bindPopup(popup).openPopup();

        // ---------------------------------- //
        // OBS: panes creation order matters! //
        // ---------------------------------- //

        // limite municipio
        myMap.createPane("paneLimiteMunicipio");
        createGEOJsonLayer(
            myMap,
            "paneLimiteMunicipio",
            limiteMunicipioData,
            "Limites",
            cityID,
            t
        );

        // zoneamento
        if (zoneamentoData[0]?.features) {
            const paneZoneamento = myMap.createPane("paneZoneamento");
            paneZoneamento.style.display = "none";
            const zoneamento = createGEOJsonLayer(
                myMap,
                "paneZoneamento",
                zoneamentoData[0].features,
                "Zoneamento",
                cityID,
                t
            );
            setZoneamentoLayer(zoneamento);
            setPanes((prevState) => ({ ...prevState, paneZoneamento }));
        }

        // semaforos conjugados
        const paneSemConj = myMap.createPane("paneSemConj");
        paneSemConj.style.display = "";
        createGEOJsonLayer(
            myMap,
            "paneSemConj",
            semConjugadosData,
            null,
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneSemConj }));

        // implantação 9
        const paneImp9 = myMap.createPane("paneImp9");
        paneImp9.style.display = "none";
        createGEOJsonLayer(myMap, "paneImp9", imp9Data, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneImp9 }));

        // implantação 8
        const paneImp8 = myMap.createPane("paneImp8");
        paneImp8.style.display = "none";
        createGEOJsonLayer(myMap, "paneImp8", imp8Data, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneImp8 }));

        // implantação 7
        const paneImp7 = myMap.createPane("paneImp7");
        paneImp7.style.display = "none";
        createGEOJsonLayer(myMap, "paneImp7", imp7Data, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneImp7 }));

        // implantação 6
        const paneImp6 = myMap.createPane("paneImp6");
        paneImp6.style.display = "none";
        createGEOJsonLayer(myMap, "paneImp6", imp6Data, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneImp6 }));

        // implantação 5
        const paneImp5 = myMap.createPane("paneImp5");
        paneImp5.style.display = "none";
        createGEOJsonLayer(myMap, "paneImp5", imp5Data, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneImp5 }));

        // implantação 4
        const paneImp4 = myMap.createPane("paneImp4");
        paneImp4.style.display = "none";
        createGEOJsonLayer(myMap, "paneImp4", imp4Data, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneImp4 }));

        // implantação 3
        const paneImp3 = myMap.createPane("paneImp3");
        paneImp3.style.display = "none";
        createGEOJsonLayer(myMap, "paneImp3", imp3Data, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneImp3 }));

        // implantação 2
        const paneImp2 = myMap.createPane("paneImp2");
        paneImp2.style.display = "none";
        createGEOJsonLayer(myMap, "paneImp2", imp2Data, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneImp2 }));

        // implantação 1
        const paneImp1 = myMap.createPane("paneImp1");
        paneImp1.style.display = "";
        createGEOJsonLayer(myMap, "paneImp1", imp1Data, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneImp1 }));

        // topografia
        const paneTopografia = myMap.createPane("paneTopografia");
        paneTopografia.style.display = "";
        createGEOJsonLayer(
            myMap,
            "paneTopografia",
            topografiaData,
            null,
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneTopografia }));

        // rede semaforica
        const paneRedeSem = myMap.createPane("paneRedeSem");
        paneRedeSem.style.display = "";
        createGEOJsonLayer(myMap, "paneRedeSem", redeSemData, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneRedeSem }));

        // sub rede semaforica
        const paneSubRedeSem = myMap.createPane("paneSubRedeSem");
        paneSubRedeSem.style.display = "";
        createGEOJsonLayer(
            myMap,
            "paneSubRedeSem",
            subRedeSemData,
            null,
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneSubRedeSem }));

        // proj interligacao layer
        const paneProjInterligacao = myMap.createPane("paneProjInterligacao");
        paneProjInterligacao.style.display = "";
        createGEOJsonLayer(
            myMap,
            "paneProjInterligacao",
            projInterligacaoData,
            null,
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneProjInterligacao }));

        // proj subtrecho layer
        const paneProjSubtrecho = myMap.createPane("paneProjSubtrecho");
        paneProjSubtrecho.style.display = "";
        const projSubLayer = createGEOJsonLayer(
            myMap,
            "paneProjSubtrecho",
            projSubtrechoData,
            "PoligonoProj",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneProjSubtrecho }));

        // area simulacao layer
        const paneAreaSim = myMap.createPane("paneAreaSim");
        paneAreaSim.style.display = "none";
        createGEOJsonLayer(
            myMap,
            "paneAreaSim",
            areasSimulacaoData,
            "PoligonoSim",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneAreaSim }));

        // rotas (corredor onibus) layer
        const paneRotas = myMap.createPane("paneRotas");
        paneRotas.style.display = "";
        const rotasLayer = createGEOJsonLayer(
            myMap,
            "paneRotas",
            rotasData,
            "Linhas",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneRotas }));

        // linhas de ônibus layer
        const paneBus = myMap.createPane("paneBus");
        paneBus.style.display = "none";
        createGEOJsonLayer(
            myMap,
            "paneBus",
            linhasOnibusData,
            "LinhasBus",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneBus }));

        // rotas desempenho layer
        const paneDesempenho = myMap.createPane("paneDesempenho");
        paneDesempenho.style.display = "none";
        createGEOJsonLayer(
            myMap,
            "paneDesempenho",
            rotasDesempenhoData,
            "Linhas",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneDesempenho }));

        // coordenacao layer
        const paneCoord = myMap.createPane("paneCoord");
        paneCoord.style.display = "";
        createGEOJsonLayer(myMap, "paneCoord", coordData, "Linhas", cityID);
        setPanes((prevState) => ({ ...prevState, paneCoord }));

        // interseccao semaforizada layer
        const paneIntSem = myMap.createPane("paneIntSem");
        paneIntSem.style.display = "";
        createPointLayer(
            myMap,
            "paneIntSem",
            intSemData,
            "interseccaoSemMap",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneIntSem }));

        // viabilidade semaforica layer
        const paneViabilidadeSem = myMap.createPane("paneViabilidadeSem");
        paneViabilidadeSem.style.display = "";
        createPointLayer(
            myMap,
            "paneViabilidadeSem",
            viabilidadeSemData,
            "viabilidadeSem",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneViabilidadeSem }));

        // interseccao nao semaforizada layer
        const paneIntNoSem = myMap.createPane("paneIntNoSem");
        paneIntNoSem.style.display = "";
        createPointLayer(
            myMap,
            "paneIntNoSem",
            intNoSemData,
            "interseccaoSemMap",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneIntNoSem }));

        // interseccao mapeada layer
        const paneIntMap = myMap.createPane("paneIntMap");
        paneIntMap.style.display = "";
        createPointLayer(
            myMap,
            "paneIntMap",
            intMapData,
            "interseccaoSemMap",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneIntMap }));

        // contagem veic layer
        const paneCont = myMap.createPane("paneCont");
        paneCont.style.display = "";
        createPointLayer(
            myMap,
            "paneCont",
            contagemVeicData,
            "contagemVeic",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneCont }));

        // contagem od layer
        const paneContagemOD = myMap.createPane("paneContagemOD");
        paneContagemOD.style.display = "";
        createPointLayer(
            myMap,
            "paneContagemOD",
            contagemODData,
            "contagemOD",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, paneContagemOD }));

        // pontos de onibus layer
        const panePontosOnibus = myMap.createPane("panePontosOnibus");
        panePontosOnibus.style.display = "";
        createPointLayer(
            myMap,
            "panePontosOnibus",
            pontosOnibusData,
            "pontosOnibus",
            cityID,
            t
        );
        setPanes((prevState) => ({ ...prevState, panePontosOnibus }));

        // rotatorias/od layer (projetos antigos, RP, Atibaia...)
        const paneContOD = myMap.createPane("paneContOD");
        paneContOD.style.display = "";
        createPointLayer(myMap, "paneContOD", contODData, null, cityID, t);
        setPanes((prevState) => ({ ...prevState, paneContOD }));

        // B1 group functions
        const setHomeView = () => {
            myMap.setView([iguLatitude, iguLongitude], iguZoom);
        };
        const setExtZoom = () => {
            if (rotasLayer) {
                myMap.fitBounds(rotasLayer.getBounds());
            } else if (projSubLayer) {
                myMap.fitBounds(projSubLayer.getBounds());
            }
        };
        const switchTiles = () => {
            if (myMap.hasLayer(tilelayer1)) {
                myMap.addLayer(tilelayer2);
                myMap.removeLayer(tilelayer1);
            } else {
                myMap.addLayer(tilelayer1);
                myMap.removeLayer(tilelayer2);
            }
        };
        const toggleLegend = () => {
            if (intLegend._map) {
                myMap.removeControl(intLegend);
            } else {
                intLegend.addTo(myMap);
            }
        };

        setB1GroupTools((prevState) => ({
            ...prevState,
            setHomeView,
            setExtZoom,
            switchTiles,
            toggleLegend,
            setSearchModalVisible,
        }));
        setB2GroupTools((prevState) => ({
            ...prevState,
            setBusLinhasLegendVisible,
            setODControlsVisible,
        }));

        return () => myMap.remove();
    }, [t]);

    return (
        <>
            <div id="map">
                {busLinhasLegendVisible && (
                    <LinhasBusLegend data={legendItems} />
                )}
                <SearchModal
                    show={searchModalVisible}
                    handleClose={() => setSearchModalVisible(false)}
                    intData={intSemData}
                    contData={contagemVeicData}
                    projData={projSubtrechoData}
                    cityID={cityID}
                    map={iguMap}
                />
                {ODControlsVisible && (
                    <ODControls map={iguMap} zoneamento={zoneamentoLayer} />
                )}
            </div>
            <IguToolbar
                cidade={userCity}
                projInfo={props.projInfo}
                b1GroupTools={b1GroupTools}
                b2GroupTools={b2GroupTools}
                panes={panes}
                layerVisibility={{ ...layerVisibility, ODControlsVisible }}
                quantities={{
                    intSem: intSemData.length,
                    intNoSem: intNoSemData.length,
                    intMap: intMapData.length,
                    viabilidadeSem: viabilidadeSemData.length,
                    contVeic: contagemVeicData.length,
                    contagemOD: contagemODData.length,
                    contOD: contODData.length,
                    projInt: projInterligacaoData.length,
                    projSub: projSubtrechoData.length,
                    areasSim: areasSimulacaoData.length,
                    rotas: rotasData.length,
                    coord: coordData.length,
                    redeSem: redeSemData.length,
                    subRedeSem: subRedeSemData.length,
                    rotasDes: rotasDesempenhoData.length,
                    linhasBus: linhasOnibusData.length,
                    odData: matrizODData.length,
                    pontosOnibus: pontosOnibusData.length,
                    topografia: topografiaData.length,
                    imp1: imp1Data.length,
                    imp2: imp2Data.length,
                    imp3: imp3Data.length,
                    imp4: imp4Data.length,
                    imp5: imp5Data.length,
                    imp6: imp6Data.length,
                    imp7: imp7Data.length,
                    imp8: imp8Data.length,
                    imp9: imp9Data.length,
                    semConj: semConjugadosData.length,
                }}
            ></IguToolbar>
        </>
    );
};

export default IguMap;
